import { Component, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from '@stencil/core';
import { Filter } from '../../03_organisms/lilu-product-list/filter';
import { FilterChangedEvent } from '../../03_organisms/lilu-product-list/filter-changed-event';
import { ActivatedFilter } from '../lilu-activated-filters/actived-filter';
import { FilterItem } from './filter-item';
import { geti18n } from '../../../utils/utils';

@Component({
  tag: 'lilu-filter-items',
  styleUrl: 'lilu-filter-items.scss',
  shadow: true,
})
export class LiluFilterItems implements Filter {
  @Prop() items: string;
  @Prop() multiSelect = false;
  @Prop() dataControlFilter: string;
  @State() internalItems: FilterItem[] = [];
  @State() foundItems: FilterItem[] = [];
  @State() searchQuery = '';

  componentWillLoad() {
    this.parseItems();
  }

  @Watch('items')
  parseItems() {
    if (this.items !== undefined) {
      this.internalItems = JSON.parse(this.items);
      this.foundItems = this.internalItems;
    }
  }

  @Event()
  filterChanged: EventEmitter<FilterChangedEvent>;

  onFilterChange(index) {
    this.internalItems[index].selected = !this.internalItems[index].selected;
    this.internalItems = [...this.internalItems]; // Update state

    const filterChangeEvent: FilterChangedEvent = {
      filterControlName: this.dataControlFilter,
      label: this.internalItems[index].label,
      value: this.internalItems[index].value,
      multiSelect: this.multiSelect,
    };
    this.filterChanged.emit(filterChangeEvent);
  }

  onFilterChangeWithChildren(index) {
    const hasUrlFilter = window.location.search.indexOf(this.internalItems[index].value) >= 0;

    if((this.internalItems[index].selected && hasUrlFilter) || (!this.internalItems[index].selected && !hasUrlFilter)) {
      this.onFilterChange(index);
    }

    if(this.internalItems[index].children) {
      for (let i = 0; i < this.internalItems[index].children.length; i++) {
        if(this.internalItems[index].children[i].selected !== this.internalItems[index].selected || this.internalItems[index].children[i].selected !== hasUrlFilter) {
          this.onFilterChangeChild(index, i);
        }
      }
    }
  }

  onFilterChangeChild(parentIndex, index) {
    this.internalItems[parentIndex].children[index].selected = !this.internalItems[parentIndex].children[index].selected;
    this.internalItems = [...this.internalItems]; // Update state

    const filterChangeEvent: FilterChangedEvent = {
      filterControlName: this.dataControlFilter,
      label: this.internalItems[parentIndex].children[index].label,
      value: this.internalItems[parentIndex].children[index].value,
      multiSelect: this.multiSelect,
    };
    this.filterChanged.emit(filterChangeEvent);

    const allChildrenSelected = this.internalItems[parentIndex].children.filter(child => child.selected).length === this.internalItems[parentIndex].children.length;
    const hasUrlFilter = window.location.search.indexOf(this.internalItems[parentIndex].value) >= 0;

    if(this.internalItems[parentIndex].selected && hasUrlFilter && !this.internalItems[parentIndex].children[index].selected) {
      this.onFilterChange(parentIndex);
    } else if(allChildrenSelected && !this.internalItems[parentIndex].selected && !hasUrlFilter) {
      this.onFilterChange(parentIndex);
    }
  }

  @Method()
  async getActiveFilterByValue(filterValue: string): Promise<ActivatedFilter> {
    return new Promise((resolve, _) => {
      // Combine the search for the item and its children in a single process
      let item = this.internalItems.find(item => {
        if (item.value === filterValue) {
          return true;
        }
        if (item.children && item.children.length > 0) {
          return item.children.some(child => child.value === filterValue);
        }
        return false;
      });

      if (item) {
        // Check if the found item is actually a child and update accordingly
        if (item.children && item.children.length > 0) {
          const child = item.children.find(child => child.value === filterValue);
          if (child) {
            item = child;
          }
        }

        const activatedFilter: ActivatedFilter = {
          filterControlName: this.dataControlFilter,
          label: item.label,
          value: item.value,
          multiSelect: this.multiSelect,
        };

        resolve(activatedFilter);
      } else {
        resolve(null);
      }
    });
  }

  @Method()
  async deactivateFilter(filterValue: string) {
    this.internalItems.forEach(filterItem => {
      if (filterItem.value === filterValue) {
        filterItem.selected = false;
      }
      if (filterItem.children && filterItem.children.length > 0) {
        filterItem.children.forEach(childItem => {
          if (childItem.value === filterValue) {
            childItem.selected = false;
          }
        });
      }
    });

    this.internalItems = [...this.internalItems]; // Update state
  }

  @Method()
  async resetAllFilters() {
    this.internalItems.forEach(filterItem => {
      filterItem.selected = false;
      if (filterItem.children && filterItem.children.length > 0) {
        filterItem.children.forEach(childItem => {
          childItem.selected = false;
        });
      }
    });

    this.internalItems = [...this.internalItems]; // Update state
  }

  onSearchFilter(event: any) {
    this.searchQuery = event.target.value.toLowerCase().trim();
  }

  onResetSearchQuery() {
    this.searchQuery = '';
  }

  render() {
    const filterItems = [];

    const getChildren = (children: FilterItem[], parentIndex: number) => {
      return children.map((child, index) => (
        <label htmlFor={`${parentIndex}-${index}`}>
          <input
            name="items"
            id={`${parentIndex}-${index}`}
            type={this.multiSelect ? 'checkbox' : 'radio'}
            checked={child.selected}
            onChange={this.onFilterChangeChild.bind(this, parentIndex, index)}
          ></input>
          <lilu-icon class="item-checkbox" color="nero" type="icon-checkbox-01"></lilu-icon>
          <span class="item-label">
            {child.label} {child.count ? `(${child.count})` : ``}
          </span>
        </label>
      ));
    };

    const hasChildSearchQuery = (children: FilterItem[]) => {
      if(this.searchQuery !== '') {
        const childrenWithQuery = children.filter(child => child.label.toLowerCase().includes(this.searchQuery));
        return childrenWithQuery.length > 0;
      }
    };

    const toggleAccordion = element => {
      const parent = element.closest('.group');

      if(parent) {
        parent.classList.toggle('open');
      }
    }

    const openChildren = (element, isSelected) => {
      const parent = element.closest('.group');

      if(parent && !isSelected) {
        parent.classList.add('open');
      }
    }
    
    for (let i = 0; i < this.internalItems.length; i++) {
      if (this.internalItems[i].label.toLowerCase().includes(this.searchQuery) || (this.internalItems[i].children && hasChildSearchQuery(this.internalItems[i].children))) {
        if (this.internalItems[i].children) {
          filterItems.push(
            <div class={`group ${this.internalItems[i].open || hasChildSearchQuery(this.internalItems[i].children) ? 'open' : ''} 
            ${this.internalItems[i].children.filter(child => child.selected).length > 0 && !this.internalItems[i].selected ? 'selected-children' : ''}`}>
              <div class={'parent'} onClick={e => toggleAccordion(e.currentTarget)}>
                <label htmlFor={`${i}`}>
                  <input
                    name="items"
                    id={`${i}`}
                    type={this.multiSelect ? 'checkbox' : 'radio'}
                    checked={this.internalItems[i].selected}
                    onChange={e => {
                      openChildren(e.currentTarget, this.internalItems[i].selected);
                      this.onFilterChangeWithChildren.bind(this, i)();
                    }}

                  ></input>
                  <lilu-icon class="item-checkbox" color="nero" type="icon-checkbox-01"></lilu-icon>
                  <span class="item-label">
                    {this.internalItems[i].label} {this.internalItems[i].count ? `(${this.internalItems[i].count})` : ``}
                  </span>
                </label>
              </div>
              <div class={`children`}>{getChildren(this.internalItems[i].children, i)}</div>
            </div>,
          );
        } else {
          filterItems.push(
            <label htmlFor={`${i}`}>
              <input
                name="items"
                id={`${i}`}
                type={this.multiSelect ? 'checkbox' : 'radio'}
                checked={this.internalItems[i].selected}
                onChange={this.onFilterChange.bind(this, i)}
              ></input>
              <lilu-icon class="item-checkbox" color="nero" type="icon-checkbox-01"></lilu-icon>
              <span class="item-label">
                {this.internalItems[i].label} {this.internalItems[i].count ? `(${this.internalItems[i].count})` : ``}
              </span>
            </label>,
          );
        }
      }
    }

    return (
      <Host>
        {this.internalItems.length > 5 && (
          <div class="search">
            <input type="text" placeholder={geti18n('filter.searchPlaceholder')} onInput={this.onSearchFilter.bind(this)} value={this.searchQuery} />
            <div class={`clear ${this.searchQuery.length > 0 ? 'show' : ''}`} onClick={this.onResetSearchQuery.bind(this)}>
              <lilu-icon size="size-14" color="nero" type="icon-closed-01" />
            </div>
          </div>
        )}
        <div class={`filter-items ${this.internalItems.length > 5 ? 'scrollbar' : ''}`}>
          {filterItems.length > 0 ? (
            filterItems
          ) : (
            <lilu-p color="nero" size="size-14" font="regular">
              {geti18n('filter.searchNotFound')}
            </lilu-p>
          )}
        </div>
      </Host>
    );
  }
}
